import Link from "next/link"
import { useRouter } from "next/router"
import { CSSProperties } from "react"
import { useDispatch } from "react-redux"
import { Card, Col, Row } from "reactstrap"

import { ISupportRequest, SupportRequestState } from "@api/schema"
import Icon from "@components/common/Icon"
import { withDynamicNamespaces } from "@components/hoc/withDynamicNamespaces"
import ProjectNameText from "@components/project/ProjectNameText"
import { SUPPORT_REQUEST_CARD_STYLE } from "@customer-config"
import { addNotificationAction } from "@redux/actions/notifications"
import { IFormikActions, updateModelAction } from "@redux/helper/actions"
import { EntityType } from "@redux/reduxTypes"
import { NamespaceShortcut, prefixedKey, useDynamicTranslation } from "@services/i18n"
import { supporterRolePictures } from "@services/partnermarketHelper"
import { Routes, routeWithParams } from "@services/routes"

import SupportOfferPostPopup from "./SupportOfferPostPopup"
import SupportRequestForm from "./SupportRequestForm"


const usedNamespaces: NamespaceShortcut[] = ["common", "partner-market", "supportRequest"]

interface IProps {
  className: string
  maxPublicDaysForSupportRequest: number
  setUpdateModeIsActive: (active: boolean) => void
  style: CSSProperties
  supportRequest: ISupportRequest
  updateMode: boolean
  withLinkToProject?: boolean
  withLinkToSupportOffers?: boolean
  withSupportOfferCreateButton?: boolean
  /**
   * should elements like buttons be accessible by keyboard? default: true.
   * Set to false, if the card is not at the front of a flipping element (@see: FlippingElement.tsx)
   */
  accessibleByKeyboard?: boolean
}

/**
 * This component provides the front card of a single SupportRequest
 */
const SupportRequestFrontCard: React.FC<IProps> = (props: IProps) => {
  const t = useDynamicTranslation()
  const {
    accessibleByKeyboard = true,
    className,
    maxPublicDaysForSupportRequest,
    setUpdateModeIsActive,
    style,
    supportRequest,
    updateMode,
    withLinkToProject = true,
    withLinkToSupportOffers = false,
    withSupportOfferCreateButton = false
  } = props
  const dispatch = useDispatch()
  const router = useRouter()

  /**
   * for customizing the SupportRequestCard
   * as string and not as enum or other imported variable,
   * b/c: ReferenceError: Cannot access 'SUPPORT_REQUEST_CARD_PROJEKTFABRIK_STYLE' before initialization
   *
   * @todo settings for processmanager: https://futureprojects.atlassian.net/browse/FCP-1329
   */
  const supportRequestCardDefaultStyle: boolean = SUPPORT_REQUEST_CARD_STYLE === "SUPPORT_REQUEST_CARD_PROJEKTFABRIK_STYLE"

  const projectSlug = supportRequest?.project?.slug || supportRequest?.project?.id

  const onUpdate = (values: ISupportRequest, actions: IFormikActions) => {
    actions.onSuccess = () => {
      dispatch(addNotificationAction(prefixedKey("partner-market", "supportRequest.updated")))
      setUpdateModeIsActive(false)
    }

    dispatch(updateModelAction(EntityType.SupportRequest, values, actions))
  }

  const onEnterOnLink = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" && accessibleByKeyboard) {
      event.stopPropagation()
      // event.preventDefault()
      void router.push(event.currentTarget.getAttribute("href"))
      // toggleFlipMode()

    }
  }

  const stateInformation = (): JSX.Element => <>
    {supportRequest.state === SupportRequestState.Public || supportRequest.state === SupportRequestState.Hidden
      ? supportRequest.publicUntil
        ? t("supportRequest", "publicUntil.detailedInfo", { date: t("common", "default.date", { value: supportRequest.publicUntil }) })
        : t("supportRequest", "state.unpublished")
      : t("supportRequest", "state." + supportRequest.state)
    }
  </>

  /**
   * to flip the card it is necessary to define a front and a back card
   * this is the front card
   */
  return <Card
    className={className}
    style={style}
  >
    <div className="support-request-card__front__body">
      {supportRequestCardDefaultStyle
        ? <>
          <div className="support-request-card__front__body__supporter-role">
            {t("supportRequest", "supporterRole." + supportRequest.supporterRole)}
          </div>
          <div className="support-request-card__front__body__request-title"><h3>{supportRequest?.title}</h3></div>
          <div className="support-request-card__front__body__supporter-role-picture" >
            <img src={supporterRolePictures[supportRequest.supporterRole].url} width="150px" height="100px" />
          </div>
        </>
        : <div className="support-request-card__front__body__supporter-role-and-title">
          <span className="support-request-card__front__body__supporter-role">
            {t("supportRequest", "supporterRole." + supportRequest.supporterRole)}
          </span>
          <span className="support-request-card__front__body__request-title"><h3>{supportRequest?.title}</h3></span>
        </div>
      }

      <div className="support-request-card__front__body__content">
        {updateMode
          ? <SupportRequestForm
            supportRequest={supportRequest}
            onSubmit={onUpdate}
            submitButtonProps={{
              title: t("partner-market", "form.submit"),
              iconName: "partner"
            }}
            cancelButtonProps={{ onClick: () => setUpdateModeIsActive(false) }}
            maxDaysPublished={maxPublicDaysForSupportRequest}
          />
          : <>
            <span className="nl2br">{supportRequest.content}</span>
            {supportRequestCardDefaultStyle && <hr />}
            <div className="support-request-card__front__body__data-and-actions first-col-auto-width">
              {withLinkToProject &&
                <>
                  <Row>
                    <Col>
                      <Icon name="project" size={25} />
                    </Col>
                    <Col>
                      <Link href={routeWithParams(Routes.ProjectPage, { slug: projectSlug })}>
                        <a
                          onClick={(e: React.MouseEvent<HTMLElement>) => {
                            e.stopPropagation()
                          }}
                          onKeyDown={onEnterOnLink}
                          // do not allow access by keyboard, if the component's elements should not be accessible:
                          // needed for flipping elements
                          tabIndex={accessibleByKeyboard ? 0 : -1}
                        >
                          <ProjectNameText project={supportRequest.project} />
                        </a>
                      </Link>
                    </Col>
                  </Row>
                </>
              }
              <Row>
                <Col>
                  <Icon name="calendar" size={25} />
                </Col>
                <Col>
                  {stateInformation()}
                </Col>
              </Row>
              <Row>
                <Col>
                  <Icon name="user" size={25} />
                </Col>
                <Col>
                  {supportRequest.updatedBy?.username}
                </Col>
              </Row>
              {withLinkToSupportOffers && <div>
                <Row>
                  <Col>
                    <Icon name="partner" size={25} />
                  </Col>
                  <Col>
                    <Link href={routeWithParams(Routes.ProjectNetworkSupportOffersOfOneSupportRequest, { slug: projectSlug, id: supportRequest.id })} passHref>
                      <a
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          e.stopPropagation()
                        }}
                        onKeyDown={onEnterOnLink}
                        // do not allow access by keyboard, if the component's elements should not be accessible:
                        // needed for flipping elements
                        tabIndex={accessibleByKeyboard ? 0 : -1}
                      >
                        {t("partner-market", "supportOffer.supportOffers")}
                      </a>
                    </Link>
                  </Col>
                </Row>
              </div>}
              {withSupportOfferCreateButton && <div className="support-request-card__front__body__supportoffer-popup-button">
                <SupportOfferPostPopup
                  supportRequest={supportRequest}
                  // do not allow access by keyboard, if the component's elements should not be accessible:
                  // needed for flipping elements
                  accessibleByKeyboard={accessibleByKeyboard}
                  showIcon={supportRequestCardDefaultStyle}
                />
              </div>}
            </div>
          </>}
      </div>
    </div >
  </Card >
}

export default withDynamicNamespaces<IProps>(SupportRequestFrontCard, usedNamespaces)
